import React, {useEffect, useRef} from 'react';
import styled from 'styled-components';
import {TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import withMemoizedContexts from '../../context/contextContainerHoC';
import useStateRef from '../../hooks/useStateRef';
import {IEmojiReactionData, IReactionGroup} from '../../model/emojiReactionsModel';
import ReactionMarker from './ReactionMarker';
import ReactionsSummaryPopup from './ReactionsSummaryPopup';
import {getReactionGroups} from './ReactionGroups';
import {reactionsStore} from '../../context/reactionsProvider';
import {useTscTheme} from '../../context/themeProvider';
import {selectedReactionGroupStore, updateSelectedReactionGroupState} from '../../context/selectedReactionProvider';
import Constants from '../../constants/Constants';

const reactionsSummaryPopupBottomOffset = 4;

interface ISmartPlayerSize {
   width: number;
   left: number;
   y: number;
}

const ReactionsPlugin = styled.div`
   top: -.5rem;
   width: 100%;
   display: flex;
   position: absolute;
`;

export const ReactionWrapper = styled.div<{leftOffset: number; theme: TscThemeNames}>`
   position: absolute;
   pointer-events: auto;
   left: ${props => props.leftOffset}%;
   transform: translateX(-50%);
`;

export const ReactionPluginBase: React.FC<IReactionsPluginProps & IStateMappedProps> = ({mediaDuration, reactions, selectedReactionGroup, selectedReactionMarkerButton}) => {
   const theme = useTscTheme();
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
   const [smartPlayerSize, setSmartPlayerSize, smartPlayerSizeRef] = useStateRef<ISmartPlayerSize>({width: 0, left: 0, y: 0});
   const smartPlayerRef = useRef(document.querySelector('.tsc-smartplayer'));
   const groupedReactions: IReactionGroup[] = getReactionGroups(reactions);

   const calculateSelectedReactionSummaryMaxHeight = () => {
      // need to get smart player client bounds everytime if UI shifts without page resize
      const playerClientRect = smartPlayerRef.current.getBoundingClientRect();
      const targetRect = selectedReactionMarkerButton.getBoundingClientRect();

      const availableHeight = targetRect.y - playerClientRect.y - Constants.reactionsSummary.topMargin;

      if (availableHeight < Constants.reactionsSummary.maxHeight) {
         return availableHeight;
      }
      return Constants.reactionsSummary.maxHeight;
   };

   const calculateSelectedReactionSummaryLeftOffset = () => {
      // need to get smart player client bounds everytime if UI shifts without page resize
      const playerClientRect = smartPlayerRef.current.getBoundingClientRect();
      const targetClientRect = selectedReactionMarkerButton.getBoundingClientRect();
      const numberEmojiToDisplay = selectedReactionGroup.reactions.length > Constants.reactionMarkers.maxNumberEmojiToPreview ? Constants.reactionMarkers.maxNumberEmojiToPreview : selectedReactionGroup.reactions.length;
      const markerWidth = Constants.reactionMarkers.emojiWidth + (Constants.reactionMarkers.emojiOffset * (numberEmojiToDisplay - 1));
      const previewHalfWidth = Constants.reactionsSummary.width / 2;
      const halfMarkerWidth = markerWidth / 2;
      const offsetX = targetClientRect.left + halfMarkerWidth - playerClientRect.left;
      const centeredOffsetX = offsetX - previewHalfWidth > 0 ? offsetX - previewHalfWidth : Constants.reactionsSummary.edgeOffset;
      const maxX = playerClientRect.width - (Constants.reactionsSummary.width + Constants.reactionsSummary.edgeOffset);
      return centeredOffsetX < maxX ? centeredOffsetX : maxX;
   };

   const onReactionSummaryClose = () => {
      updateSelectedReactionGroupState({selectedReactionGroup: null, selectedReactionMarkerButton: null});
   };

   useEffect(() => {
      let smartPlayerSizeObserver: ResizeObserver;

      const addObserver = () => {
         if (!smartPlayerRef.current) {
            smartPlayerRef.current = document.querySelector('.tsc-smartplayer');
            if (!smartPlayerRef.current) {
               return;
            }
         }

         smartPlayerSizeObserver = new ResizeObserver(() => {
            const playerRect = smartPlayerRef.current.getBoundingClientRect();
            if (smartPlayerSizeRef.current.width !== Math.ceil(playerRect.width) || smartPlayerSizeRef.current.left !== Math.ceil(playerRect.left)) {
               setSmartPlayerSize({
                  width: Math.ceil(playerRect.width),
                  left: Math.ceil(playerRect.left),
                  y: playerRect.y
               });
            }
         });
         smartPlayerSizeObserver.observe(document.body);
      };

      addObserver();

      return () => {
         if (smartPlayerSizeObserver) {
            smartPlayerSizeObserver.disconnect();
         }
      };
   }, []);

   return (
      <ReactionsPlugin data-test-id="reaction-plugin">
         {!!mediaDuration && groupedReactions && groupedReactions.length > 0 && groupedReactions.map(reactionGroup => (
            <ReactionWrapper
               key={`reaction-wrapper-${reactionGroup.startTime}`}
               theme={theme}
               leftOffset={(Math.max(Math.min(reactionGroup.startTime / mediaDuration * 100, 100), 0))}
            >
               <ReactionMarker reactionGroup={reactionGroup} />
            </ReactionWrapper>
         ))}
         {selectedReactionGroup && selectedReactionMarkerButton && (
            <ReactionsSummaryPopup
               reactionsToDisplay={selectedReactionGroup.reactions}
               leftOffset={calculateSelectedReactionSummaryLeftOffset()}
               bottomOffset={reactionsSummaryPopupBottomOffset}
               maxHeight={calculateSelectedReactionSummaryMaxHeight()}
               closeOnInteractions={true}
               onClose={onReactionSummaryClose}
            />
         )}
      </ReactionsPlugin>
   );
};

export interface IReactionsPluginProps {
   mediaDuration: number;
}

export interface IStateMappedProps {
   reactions: IEmojiReactionData[];
   selectedReactionGroup: IReactionGroup;
   selectedReactionMarkerButton: HTMLButtonElement;
}

const mapStatesToProps = (
   {mediaDuration}: IReactionsPluginProps,
   {reactions}: Partial<IStateMappedProps>,
   {selectedReactionGroup, selectedReactionMarkerButton}: Partial<IStateMappedProps>
): IReactionsPluginProps & IStateMappedProps => ({mediaDuration, reactions, selectedReactionGroup, selectedReactionMarkerButton});

export default withMemoizedContexts(mapStatesToProps, reactionsStore, selectedReactionGroupStore)(ReactionPluginBase);
